import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import { ChevronLeft, ChevronRight, Eye, FilePlus2, Paperclip, Search, Upload, X } from 'lucide-react';
import FormPageShell from '../../components/Common/FormPageShell';
import PageHeader from '../../components/Common/PageHeader';
import QueryErrorBanner from '../../components/Common/QueryErrorBanner';
import EmptyState from '../../components/Common/EmptyState';
import { getAppHomePath } from '../../constants/navigation';
import { useAuth } from '../../contexts/AuthContext';
import { usePagination } from '../../hooks/useApi';
import { processService } from '../../services/api';
import { isRequesterRole } from '../../utils/tenantProfile';

const MAX_FILE_SIZE = 20 * 1024 * 1024;
const thClass =
  'px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 border-b border-gray-200 bg-gray-50';
const tdClass = 'px-3 py-3 text-sm text-gray-900 border-b border-gray-100 align-top';

function unwrap(payload) {
  return Array.isArray(payload) ? payload : payload?.data ?? payload?.items ?? [];
}

function formatWhen(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('pt-BR');
}

function formatSize(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function Pedidos() {
  const { user } = useAuth();
  const requester = isRequesterRole(user);
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [texto, setTexto] = useState('');
  const [files, setFiles] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [search, setSearch] = useState('');
  const pagination = usePagination(1, 10);
  const { data, isLoading, isError, error } = useQuery(['pedidos'], () => processService.listPedidos());
  const rows = unwrap(data);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((row) => {
      const anexos = (row.anexos || []).map((anexo) => anexo.filename_original).join(' ');
      return `${row.numero || ''} ${row.texto || ''} ${row.processo_numero || ''} ${anexos}`
        .toLowerCase()
        .includes(q);
    });
  }, [rows, search]);

  const paged = useMemo(() => {
    const start = (pagination.page - 1) * pagination.limit;
    return filtered.slice(start, start + pagination.limit);
  }, [filtered, pagination.page, pagination.limit]);

  useEffect(() => {
    pagination.setTotal(filtered.length);
    pagination.setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered.length, search]);

  const addFiles = (incoming) => {
    const next = [];
    Array.from(incoming || []).forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        toast.error(`${file.name} passa de 20 MB.`);
        return;
      }
      const exists = files.some(
        (current) => current.name === file.name && current.size === file.size && current.lastModified === file.lastModified
      );
      if (!exists) next.push(file);
    });
    if (next.length) setFiles((current) => [...current, ...next]);
  };

  const create = useMutation(
    async () => {
      const form = new FormData();
      form.append('texto', texto.trim());
      files.forEach((file) => form.append('files', file));
      return processService.createPedido(form);
    },
    {
      onSuccess: (row) => {
        toast.success(
          row?.processo_numero
            ? `Pedido enviado. Processo ${row.processo_numero} aberto.`
            : 'Pedido enviado.'
        );
        setTexto('');
        setFiles([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
        queryClient.invalidateQueries(['pedidos']);
        queryClient.invalidateQueries(['portal-pedidos']);
      },
      onError: (err) => {
        const detail = err?.response?.data?.detail;
        toast.error(typeof detail === 'string' ? detail : 'Não foi possível enviar o pedido.');
      },
    }
  );

  const openAnexo = async (anexo) => {
    try {
      const response = await processService.downloadPedidoAnexo(anexo.id);
      const blob = new Blob([response.data], { type: anexo.mime_type || 'application/octet-stream' });
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch {
      toast.error('Não foi possível abrir o documento.');
    }
  };

  return (
    <FormPageShell maxWidth="w-full max-w-none" className="!p-0">
      <div className="space-y-6">
        <PageHeader
          title="Pedidos"
          description={
            requester
              ? 'Descreva a solicitação e, se precisar, anexe documentos. O órgão recebe um processo do tipo Diversos já vinculado a você.'
              : 'Solicitações diversas enviadas pelos requerentes. Cada pedido já abre um processo Diversos.'
          }
          breadcrumbs={[{ label: 'Início', to: getAppHomePath() }, { label: 'Pedidos' }]}
        />

        {isError ? <QueryErrorBanner error={error} /> : null}

        {requester ? (
          <form
            className="data-list-card p-5 sm:p-6"
            onSubmit={(event) => {
              event.preventDefault();
              if (texto.trim().length < 3) {
                toast.error('Descreva o pedido.');
                return;
              }
              create.mutate();
            }}
          >
            <div className="form-group">
              <label className="form-label" htmlFor="pedido-texto">
                O que você precisa solicitar?
              </label>
              <textarea
                id="pedido-texto"
                className="form-input min-h-32"
                value={texto}
                onChange={(event) => setTexto(event.target.value)}
                placeholder="Explique o pedido com o máximo de detalhe que o órgão precisa para analisar."
                required
              />
            </div>

            <div className="form-group mt-4">
              <span className="form-label">Documentos</span>
              <div
                className={`mt-2 cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
                  dragActive ? 'border-primary-500 bg-primary-50' : 'border-gray-300 hover:border-gray-400'
                }`}
                onClick={() => fileInputRef.current?.click()}
                onDragEnter={(event) => {
                  event.preventDefault();
                  setDragActive(true);
                }}
                onDragOver={(event) => {
                  event.preventDefault();
                  setDragActive(true);
                }}
                onDragLeave={(event) => {
                  event.preventDefault();
                  setDragActive(false);
                }}
                onDrop={(event) => {
                  event.preventDefault();
                  setDragActive(false);
                  addFiles(event.dataTransfer.files);
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    fileInputRef.current?.click();
                  }
                }}
              >
                <Upload className="mx-auto mb-3 h-10 w-10 text-gray-400" />
                <p className="text-base font-medium text-gray-900">Arraste os arquivos ou clique para selecionar</p>
                <p className="mt-1 text-sm text-gray-500">Vários documentos · até 20 MB cada</p>
                <input
                  ref={fileInputRef}
                  id="pedido-arquivos"
                  className="hidden"
                  type="file"
                  multiple
                  onChange={(event) => {
                    addFiles(event.target.files);
                    event.target.value = '';
                  }}
                />
              </div>
              {files.length ? (
                <ul className="mt-3 space-y-2">
                  {files.map((file) => (
                    <li
                      key={`${file.name}-${file.size}-${file.lastModified}`}
                      className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 px-3 py-2"
                    >
                      <span className="min-w-0 truncate text-sm text-gray-800">
                        {file.name}
                        <span className="ml-2 text-gray-500">{formatSize(file.size)}</span>
                      </span>
                      <button
                        type="button"
                        className="rounded p-1 text-gray-400 hover:text-red-600"
                        aria-label={`Remover ${file.name}`}
                        onClick={() =>
                          setFiles((current) =>
                            current.filter(
                              (item) =>
                                !(item.name === file.name && item.size === file.size && item.lastModified === file.lastModified)
                            )
                          )
                        }
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>

            <button
              type="submit"
              className="btn btn-primary mt-4 inline-flex items-center gap-2"
              disabled={create.isLoading}
            >
              <FilePlus2 className="h-4 w-4" />
              {create.isLoading ? 'Enviando…' : 'Enviar pedido'}
            </button>
          </form>
        ) : null}

        <div className="data-list-card overflow-visible">
          <div className="border-b border-gray-100 bg-gradient-to-r from-slate-50/80 to-white px-5 py-4 sm:px-6">
            <div className="form-group max-w-xl">
              <label className="form-label" htmlFor="pedido-search">
                Busca
              </label>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input
                  id="pedido-search"
                  className="form-input pl-9"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Número, texto ou processo"
                />
              </div>
            </div>
          </div>

          <div className="space-y-4 p-5 sm:p-6">
            <div className="text-sm text-gray-600">
              Mostrando {filtered.length} pedido(s) {rows.length !== filtered.length ? '(filtrados)' : ''}
            </div>

            {isLoading ? <p className="text-sm text-gray-600">Carregando pedidos…</p> : null}

            {!isLoading && filtered.length === 0 ? (
              <EmptyState
                icon={FilePlus2}
                title="Nenhum pedido encontrado"
                description={
                  search
                    ? 'Nenhum pedido corresponde à busca.'
                    : requester
                      ? 'Descreva a solicitação acima para abrir o primeiro pedido.'
                      : 'Quando um requerente enviar uma solicitação diversa, ela aparece aqui.'
                }
              />
            ) : null}

            {!isLoading && filtered.length > 0 ? (
              <div>
                <div className="-mx-5 overflow-x-auto sm:-mx-6">
                  <table className="w-full min-w-[56rem] border-collapse">
                    <thead>
                      <tr>
                        <th className={`${thClass} whitespace-nowrap`}>Número</th>
                        <th className={`${thClass} whitespace-nowrap`}>Processo</th>
                        <th className={`${thClass} min-w-[16rem]`}>Solicitação</th>
                        <th className={`${thClass} min-w-[12rem]`}>Anexos</th>
                        <th className={`${thClass} whitespace-nowrap`}>Data</th>
                        <th className={`${thClass} sticky right-0 z-20 whitespace-nowrap shadow-[-8px_0_12px_-8px_rgba(15,23,42,0.18)]`}>
                          Ações
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map((row) => (
                        <tr key={row.id} className="group hover:bg-gray-50">
                          <td className={`${tdClass} whitespace-nowrap font-medium`}>{row.numero}</td>
                          <td className={`${tdClass} whitespace-nowrap`}>
                            {row.processo_numero ? (
                              <Link
                                to={`/process/cases/${row.processo_id}`}
                                className="font-medium text-primary-700 hover:text-primary-900"
                              >
                                {row.processo_numero}
                              </Link>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className={`${tdClass} min-w-[16rem] max-w-md whitespace-normal break-normal`}>
                            <p className="line-clamp-3" title={row.texto}>
                              {row.texto}
                            </p>
                          </td>
                          <td className={`${tdClass} min-w-[12rem]`}>
                            {row.anexos?.length ? (
                              <ul className="space-y-1">
                                {row.anexos.map((anexo) => (
                                  <li key={anexo.id}>
                                    <button
                                      type="button"
                                      className="inline-flex max-w-[16rem] items-center gap-1 truncate text-left text-sm text-primary-700 hover:text-primary-900"
                                      onClick={() => openAnexo(anexo)}
                                    >
                                      <Paperclip className="h-3.5 w-3.5 shrink-0" />
                                      <span className="truncate">{anexo.filename_original}</span>
                                    </button>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <span className="text-gray-500">—</span>
                            )}
                          </td>
                          <td className={`${tdClass} whitespace-nowrap text-gray-600`}>{formatWhen(row.created_at)}</td>
                          <td
                            className={`${tdClass} sticky right-0 z-10 whitespace-nowrap bg-white shadow-[-8px_0_12px_-8px_rgba(15,23,42,0.12)] group-hover:bg-gray-50`}
                          >
                            {row.processo_id ? (
                              <div className="flex justify-end">
                                <Link
                                  to={`/process/cases/${row.processo_id}`}
                                  className="btn btn-outline btn-sm btn-icon"
                                  title="Abrir processo"
                                >
                                  <Eye className="h-4 w-4" />
                                </Link>
                              </div>
                            ) : null}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="pagination border-t border-gray-200 px-1 py-4 sm:px-2">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="text-sm text-gray-600">
                      Mostrando {filtered.length ? (pagination.page - 1) * pagination.limit + 1 : 0} a{' '}
                      {Math.min(pagination.page * pagination.limit, filtered.length)} de {filtered.length} pedidos
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={pagination.prevPage}
                        disabled={!pagination.hasPrev}
                        className="pagination-btn"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </button>
                      {Array.from({ length: Math.min(5, pagination.totalPages || 1) }, (_, i) => {
                        const page = i + 1;
                        return (
                          <button
                            key={page}
                            type="button"
                            onClick={() => pagination.goToPage(page)}
                            className={`pagination-btn ${pagination.page === page ? 'active' : ''}`}
                          >
                            {page}
                          </button>
                        );
                      })}
                      <button
                        type="button"
                        onClick={pagination.nextPage}
                        disabled={!pagination.hasNext}
                        className="pagination-btn"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </FormPageShell>
  );
}
